import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useCondoStore } from '../store/condoStore';
import { staticApi } from '../utils/staticApi';
import { Condo } from '../types';

const { Title } = Typography;
const { Option } = Select;

export const Condos: React.FC = () => {
  const {
    condos,
    isLoading,
    fetchCondos,
    createCondo,
    updateCondo,
    deleteCondo,
  } = useCondoStore();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCondo, setEditingCondo] = useState<Condo | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCondos();
  }, [fetchCondos]);

  const handleCreate = () => {
    setEditingCondo(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (condo: Condo) => {
    setEditingCondo(condo);
    form.setFieldsValue(condo);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCondo(id);
      staticApi.message.success('Condo deleted successfully');
    } catch (error) {
      staticApi.message.error('Failed to delete condo');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCondo) {
        await updateCondo(editingCondo.id, values);
        staticApi.message.success('Condo updated successfully');
      } else {
        await createCondo(values);
        staticApi.message.success('Condo created successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      staticApi.message.error('Operation failed');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <HomeOutlined style={{ color: '#1890ff' }} />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (text: string, record: Condo) => (
        <span>{`${text}, ${record.city}, ${record.state} ${record.zipCode}`}</span>
      ),
    },
    {
      title: 'Total Units',
      dataIndex: 'totalUnits',
      key: 'totalUnits',
      align: 'center' as const,
    },
    {
      title: 'Monthly Fee',
      dataIndex: 'monthlyFee',
      key: 'monthlyFee',
      render: (amount: number) => `$${amount}`,
      align: 'right' as const,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Condo) => ( // Use 'unknown' type for _
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this condo?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Condominium Management</Title>
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Condos"
                value={condos.length}
                prefix={<HomeOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Active Condos"
                value={condos.filter(c => c.isActive).length}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Units"
                value={condos.reduce((sum, condo) => sum + condo.totalUnits, 0)}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Average Monthly Fee"
                value={condos.length > 0 ? 
                  condos.reduce((sum, condo) => sum + condo.monthlyFee, 0) / condos.length : 0
                }
                precision={2}
                prefix="$"
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Card
        title="All Condominiums"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add New Condo
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={condos}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingCondo ? 'Edit Condo' : 'Add New Condo'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Condo Name"
                rules={[{ required: true, message: 'Please enter condo name' }]}
              >
                <Input placeholder="e.g., Ocean View Towers" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="totalUnits"
                label="Total Units"
                rules={[{ required: true, message: 'Please enter total units' }]}
              >
                <Input type="number" placeholder="50" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Street Address"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input placeholder="123 Main Street" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input placeholder="Miami" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true, message: 'Please enter state' }]}
              >
                <Input placeholder="FL" maxLength={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="zipCode"
                label="ZIP Code"
                rules={[{ required: true, message: 'Please enter ZIP code' }]}
              >
                <Input placeholder="33139" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contactEmail"
                label="Contact Email"
                rules={[
                  { required: true, message: 'Please enter contact email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="manager@condo.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactPhone"
                label="Contact Phone"
                rules={[{ required: true, message: 'Please enter contact phone' }]}
              >
                <Input placeholder="+1 (555) 123-4567" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="monthlyFee"
                label="Monthly Maintenance Fee"
                rules={[{ required: true, message: 'Please enter monthly fee' }]}
              >
                <Input type="number" prefix="$" placeholder="350" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Status"
                initialValue={true}
              >
                <Select>
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button onClick={() => { setIsModalVisible(false); form.resetFields(); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCondo ? 'Update' : 'Add'} Condo
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};